/**
 * StatusChip
 * 
 * Wraps MUI Chip with status-to-tone binding.
 * Resolves status → tone → theme color via statusTone.js.
 * 
 * Used by: N2 (customer list), N5 (project list), N7 (marker popup), N13 (entry log)
 * 
 * Props:
 *   status: string — the status value to display
 *   domain: string — one of: projectStatus, countFreshness, entryChannel, capability, supplyMode
 *   size: 'small' | 'medium' (default: 'small')
 *   variant: 'filled' | 'outlined' (default: 'filled')
 */

import React from 'react';
import { Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { resolveStatusTone } from '../../utils/statusTone';

export default function StatusChip({
  status,
  domain = 'projectStatus',
  size = 'small',
  variant = 'filled',
  label: labelOverride,
  sx = {},
  ...rest
}) {
  const theme = useTheme();
  const { label, themeColor } = resolveStatusTone(domain, status);

  // Resolve theme color path (e.g., 'success.main' → theme.palette.success.main)
  const resolveColor = (colorPath) => {
    const parts = colorPath.split('.');
    let result = theme.palette;
    for (const part of parts) {
      result = result?.[part];
    }
    return result || theme.palette.grey[500];
  };

  const color = resolveColor(themeColor);

  return (
    <Chip
      label={labelOverride || label}
      size={size}
      variant={variant}
      sx={{
        fontWeight: 500,
        ...(variant === 'filled' ? {
          backgroundColor: alpha(color, 0.12), // 12% opacity background
          color: color,
          border: `1px solid ${alpha(color, 0.25)}`, // 25% opacity border
        } : {
          borderColor: color,
          color: color,
        }),
        ...sx,
      }}
      {...rest}
    />
  );
}
